/**
 * Lettura della versione NATIVA dell'app (binario Despia) a runtime e confronto
 * semver, per il gate di aggiornamento forzato.
 *
 * Nota: l'app è una WebView Despia. Il codice web si aggiorna da solo a ogni
 * deploy Cloudflare; questo modulo serve SOLO a capire la versione del guscio
 * nativo (App Store / Play Store), che cambia con un nuovo rilascio Despia.
 */
import despia from "despia-native";

/** Piattaforma corrente in base allo User-Agent. */
export function detectPlatform(): "ios" | "android" | "web" {
  if (typeof navigator === "undefined") return "web";
  const ua = navigator.userAgent.toLowerCase();
  const isDespia = ua.includes("despia");
  if (!isDespia) return "web";
  if (ua.includes("iphone") || ua.includes("ipad")) return "ios";
  if (ua.includes("android")) return "android";
  return "web";
}

/**
 * Legge la versione nativa (marketing version, es. "1.2.3") tramite il bridge
 * Despia: `despia('getappversion://', ['versionNumber'])` risolve con
 * `{ versionNumber }`. Lo User-Agent Despia è solo "despia-iphone" e NON
 * contiene la versione, quindi il bridge è l'unica fonte affidabile.
 *
 * Ritorna null se non disponibile (fuori dall'app nativa, timeout, formato
 * inatteso). IMPORTANTE: chi chiama deve trattare null come "non bloccare"
 * (fail-open), così non si chiudono mai fuori gli utenti per un errore.
 */
/** Legge un singolo campo da getappversion:// (watch a 1 variabile = timeout
 *  interno del SDK, niente hang) con gara di sicurezza a 3s. */
async function readAppVersionField(key: string): Promise<string | null> {
  try {
    const info = await Promise.race([
      despia("getappversion://", [key]) as Promise<Record<string, unknown>>,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)),
    ]);
    const v = info ? (info as Record<string, unknown>)[key] : undefined;
    if (v == null || v === "n/a") return null;
    return String(v).trim() || null;
  } catch {
    return null;
  }
}

/** Entrambi i campi versione del bridge Despia, per capire quale è la marketing
 *  version (es. "1.1.4") e quale il build number (es. "07170911"). */
export async function getNativeAppInfo(): Promise<{
  versionNumber: string | null;
  bundleNumber: string | null;
}> {
  if (detectPlatform() === "web") return { versionNumber: null, bundleNumber: null };
  const versionNumber = await readAppVersionField("versionNumber");
  const bundleNumber = await readAppVersionField("bundleNumber");
  return { versionNumber, bundleNumber };
}

/** Versione marketing nativa (semver, es. "1.1.4") per il confronto force-update.
 *  Ritorna null se non disponibile → chi chiama fa fail-open. */
export async function getNativeAppVersion(): Promise<string | null> {
  if (detectPlatform() === "web") return null;
  const { versionNumber, bundleNumber } = await getNativeAppInfo();
  // Preferiamo il campo che ha forma semver (x.y / x.y.z); il build number a
  // data (8 cifre senza punti) non va confrontato con la versione dello store.
  const isSemver = (s: string | null): s is string => !!s && /^\d+\.\d+(\.\d+)?$/.test(s);
  if (isSemver(versionNumber)) return versionNumber;
  if (isSemver(bundleNumber)) return bundleNumber;
  return null;
}

/**
 * Confronto semver. Ritorna -1 se a<b, 0 se uguali, 1 se a>b.
 * Tollera lunghezze diverse ("1.2" vs "1.2.0") e parti non numeriche.
 */
export function compareVersions(a: string, b: string): number {
  const pa = a.split(".").map((x) => parseInt(x, 10) || 0);
  const pb = b.split(".").map((x) => parseInt(x, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const na = pa[i] ?? 0;
    const nb = pb[i] ?? 0;
    if (na < nb) return -1;
    if (na > nb) return 1;
  }
  return 0;
}
